/**
 * CurrencyInput - controlled money input that produces ``{amount, currency}``.
 *
 * The canonical money/currency input across Raven. Backend MONEY /
 * CURRENCY fields persist as ``{amount: number, currency: 'GBP'}``
 * dicts; this is the only input that produces that shape directly.
 * Two consumers wrap it:
 *
 *   - ``CurrencyField`` (form wrapper in
 *     ``@/shared/lib/forms/generated-input``) for fields driven by
 *     ``useForm``;
 *   - ``MetadataField`` (controlled wrapper in
 *     ``@/shared/components/metadata-field``) for fields rendered
 *     outside ``useForm``.
 *
 * Behaviour ported verbatim from the original form-bound
 * ``CurrencyField`` so both code paths now share one implementation:
 *
 *   * Currency dropdown defaults to the tenant's currency
 *     (``useFormattingContext().currency``) and offers exactly the
 *     currencies the organisation has switched on
 *     (``OrganisationPreferences.available_currencies``, surfaced as
 *     ``useFormattingContext().availableCurrencies``). There is no
 *     hardcoded currency list: an org that selected only USD + CAD must
 *     never be offered GBP / EUR / AUD (TA-19).
 *   * Per-field options from the backend (a MONEY attribute's
 *     ``configuration_data.options``) take priority over the org set.
 *   * Live-formats the typed amount per locale (grouping separators,
 *     decimal marker).
 *   * Cursor restoration so insertion / deletion of grouping
 *     separators doesn't shove the caret around mid-edit.
 *   * Currency-symbol prefix derived from
 *     ``Intl.NumberFormat(locale, { style: 'currency' })``.
 *
 * Defaults the missing ``currency`` on the first mount so a submit
 * without ever opening the dropdown still ships the tenant currency.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  resolveCurrencyOptions,
  type CurrencyOption,
} from '@/shared/components/ui/currency-options'
import { useFormattingContext } from '@/shared/lib/formatting'
import { cn } from '@/shared/lib/utils'

export interface CurrencyValue {
  amount?: number
  currency?: string
}

export type { CurrencyOption }

export interface CurrencyInputProps {
  /** Current value. ``null`` / ``undefined`` is treated as empty; a bare
   *  number or numeric string (older stored values) is read as the amount. */
  value: CurrencyValue | number | string | null | undefined
  /** Called with the next ``{amount, currency}`` value on every change. */
  onChange: (value: CurrencyValue) => void
  /** Optional per-field currency option list (a MONEY attribute's
   *  ``configuration_data.options``); falls back to the org's selected
   *  currencies (``useFormattingContext().availableCurrencies``). */
  currencyOptions?: CurrencyOption[]
  placeholder?: string
  disabled?: boolean
  /** Optional className applied to the amount input wrapper. */
  className?: string
  onBlur?: () => void
  /** Paint the error border. Matches PhoneInput / DatePicker. */
  hasError?: boolean
  inputId?: string
  inputTestId?: string
}

function getCurrencySymbol(code: string, locale: string): string {
  try {
    return (
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
        .formatToParts(0)
        .find((p) => p.type === 'currency')?.value ?? code
    )
  } catch {
    return code
  }
}

function getLocaleSeparators(locale: string): { group: string; decimal: string } {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(1234.5)
    return {
      group: parts.find((p) => p.type === 'group')?.value ?? ',',
      decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
    }
  } catch {
    return { group: ',', decimal: '.' }
  }
}

function formatLiveAmount(text: string, locale: string): string {
  if (!text) return ''
  const { group, decimal } = getLocaleSeparators(locale)
  const noGroups = text.split(group).join('')
  const [intPart = '', ...rest] = noGroups.split(decimal)
  const decPart = rest.join('')
  const intNum = Number.parseInt(intPart, 10)
  const intFormatted =
    Number.isFinite(intNum) && intPart.length > 0
      ? new Intl.NumberFormat(locale, {
          maximumFractionDigits: 0,
          useGrouping: true,
        }).format(intNum)
      : intPart
  return noGroups.includes(decimal) ? `${intFormatted}${decimal}${decPart}` : intFormatted
}

function parseLocaleAmount(text: string, locale: string): number | undefined {
  if (!text) return undefined
  const { group, decimal } = getLocaleSeparators(locale)
  const noGroups = text.split(group).join('')
  const normalised = decimal === '.' ? noGroups : noGroups.split(decimal).join('.')
  const num = Number.parseFloat(normalised)
  return Number.isFinite(num) ? num : undefined
}

/**
 * Money values stored before the ``{amount, currency}`` shape are a bare
 * ``"5000.00"``. Read those as the amount: treating them as empty blanked the
 * field, and the mount effect below then wrote back ``{currency}`` alone, so
 * saving the form erased a price nobody had touched.
 */
function toCurrencyValue(value: CurrencyInputProps['value']): CurrencyValue {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? { amount: value } : {}
  }
  if (typeof value === 'string') {
    const amount = Number(value.trim())
    return value.trim() !== '' && Number.isFinite(amount) ? { amount } : {}
  }
  return value ?? {}
}

export function CurrencyInput({
  value,
  onChange,
  currencyOptions,
  placeholder = 'Amount',
  disabled,
  className,
  onBlur,
  hasError = false,
  inputId,
  inputTestId,
}: CurrencyInputProps) {
  const {
    currency: orgCurrency,
    availableCurrencies,
    locale,
  } = useFormattingContext()
  const safeValue: CurrencyValue = toCurrencyValue(value)

  const effectiveCurrency = safeValue.currency || orgCurrency

  const resolvedOptions = useMemo<CurrencyOption[]>(
    () => resolveCurrencyOptions(currencyOptions, availableCurrencies, effectiveCurrency),
    [currencyOptions, availableCurrencies, effectiveCurrency],
  )
  const symbol = useMemo(
    () => getCurrencySymbol(effectiveCurrency, locale),
    [effectiveCurrency, locale],
  )
  const { group: groupSep } = useMemo(() => getLocaleSeparators(locale), [locale])

  const inputRef = useRef<HTMLInputElement>(null)
  const cursorRestoreRef = useRef<number | null>(null)
  const [displayValue, setDisplayValue] = useState(() =>
    safeValue.amount !== undefined
      ? formatLiveAmount(String(safeValue.amount), locale)
      : '',
  )

  // Re-format the display when the underlying value changes from outside
  // (parent reset / programmatic update). Skip while the input is
  // focused so we don't clobber what the user is typing.
  useEffect(() => {
    if (document.activeElement === inputRef.current) return
    setDisplayValue(
      safeValue.amount !== undefined
        ? formatLiveAmount(String(safeValue.amount), locale)
        : '',
    )
  }, [safeValue.amount, locale])

  // Restore the cursor after React commits a new displayValue.
  // Intentionally no deps array: must run after *every* render so we
  // can restore the caret immediately after React commits the new
  // formatted text. The ref guard makes the body a no-op except right
  // after a keystroke.
  useEffect(() => {
    if (cursorRestoreRef.current !== null && inputRef.current) {
      const pos = cursorRestoreRef.current
      inputRef.current.setSelectionRange(pos, pos)
      cursorRestoreRef.current = null
    }
  })

  // Persist the resolved default currency on mount so a submit without
  // ever opening the dropdown still ships a currency.
  const initialisedRef = useRef(false)
  useEffect(() => {
    if (initialisedRef.current) return
    initialisedRef.current = true
    if (!safeValue.currency) {
      onChange({ ...safeValue, currency: effectiveCurrency })
    }
    // Run once on mount - value is captured at first render and we
    // intentionally don't re-fire when the user clears the field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={className}>
      <div className="flex w-full">
        <Select
          value={effectiveCurrency}
          disabled={disabled}
          onValueChange={(currency: string) =>
            onChange({ ...safeValue, currency })
          }
        >
          <SelectTrigger
            data-slot="currency-select-trigger"
            className="w-[68px] shrink-0 rounded-r-none border-r-0 bg-[var(--bg-subtle)] text-[var(--ink-2)] focus-visible:z-10 focus-visible:relative"
          >
            <SelectValue placeholder="Currency" />
          </SelectTrigger>
          <SelectContent>
            {resolvedOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-0">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-0 inline-block text-right w-[32px] top-1/2 z-20 -translate-y-1/2 text-[13px] text-[var(--ink-2)]"
          >
            {symbol}
          </span>
          <Input
            ref={inputRef}
            id={inputId}
            data-testid={inputTestId}
            type="text"
            inputMode="decimal"
            value={displayValue}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            className={cn(
              'rounded-l-none pl-9 focus-visible:z-10 focus-visible:relative !py-1.5',
              hasError && 'border-destructive',
            )}
            onBlur={onBlur}
            onChange={(event) => {
              const el = event.target
              let raw = el.value
              let cursorPos = el.selectionStart ?? raw.length

              // If the user just deleted a group separator with
              // backspace (e.g. caret was right after the comma in
              // "1,234" and they hit ⌫), the live formatter would
              // otherwise re-insert it and leave the cursor stuck.
              // Detect that case and drop the digit the comma was
              // anchored to instead.
              if (raw.length === displayValue.length - 1) {
                let diffIdx = 0
                while (diffIdx < raw.length && raw[diffIdx] === displayValue[diffIdx])
                  diffIdx++
                if (displayValue[diffIdx] === groupSep && cursorPos > 0) {
                  raw = raw.slice(0, cursorPos - 1) + raw.slice(cursorPos)
                  cursorPos -= 1
                }
              }

              const sigCharsBefore = raw
                .slice(0, cursorPos)
                .split(groupSep)
                .join('').length

              const formatted = formatLiveAmount(raw, locale)

              let newCursor = 0
              let cnt = 0
              while (newCursor < formatted.length && cnt < sigCharsBefore) {
                if (formatted[newCursor] !== groupSep) cnt++
                newCursor++
              }

              setDisplayValue(formatted)
              cursorRestoreRef.current = newCursor

              const parsed = parseLocaleAmount(formatted, locale)
              onChange({
                ...safeValue,
                amount: parsed,
                currency: effectiveCurrency,
              })
            }}
          />
        </div>
      </div>
    </div>
  )
}
